import React, { useEffect, useState } from 'react';
import { DentosHeader } from '../components/DentosHeader';
import { DentosFooter } from '../components/DentosFooter';
import { ChevronDown, ChevronUp, ArrowRight, ArrowUp } from 'lucide-react';
type PageType = 'home' | 'knowledge' | 'tools' | 'research' | 'dental-kids';
interface DentalKidsPageProps {
  onNavigate: (page: PageType) => void;
}
const features = [{
  icon: '🎮',
  iconBg: '#2B7FE0',
  title: 'Геймификация лечения',
  description: 'Ребёнок получает баллы за каждый визит, квесты за чистку зубов дома, уровни и награды.',
  benefit: 'Частота визитов ×2-3',
  benefitColor: '#16a34a'
}, {
  icon: '📅',
  iconBg: '#F97316',
  title: 'Онлайн-запись и напоминания',
  description: 'Родитель записывается в два клика. Автоматические напоминания за 48 часов и 2 часа до визита.',
  benefit: 'No-show −60%',
  benefitColor: '#16a34a'
}, {
  icon: '📊',
  iconBg: '#00C2D4',
  title: 'Дневник здоровья зубов',
  description: 'Родитель видит прогресс лечения, историю визитов, рекомендации врача и следующие шаги.',
  benefit: 'Согласие на лечение ×2.3',
  benefitColor: '#16a34a'
}, {
  icon: '👨‍👩‍👧',
  iconBg: '#7c3aed',
  title: 'Семейный профиль',
  description: 'Один аккаунт для всей семьи. Мама добавила ребёнка — приложение предлагает записать и её, и всю семью.',
  benefit: '+2.4 взрослых пациента',
  benefitColor: '#16a34a'
}, {
  icon: '🏆',
  iconBg: '#d97706',
  title: 'Программа лояльности',
  description: 'Баллы за визиты, реферальная программа, специальные предложения для постоянных семей.',
  benefit: 'Удержание 88%',
  benefitColor: '#16a34a'
}, {
  icon: '📱',
  iconBg: '#dc2626',
  title: 'Push-уведомления и контент',
  description: 'Полезные советы, напоминания о плановых осмотрах, поздравления с днём рождения.',
  benefit: '8-12 открытий в месяц',
  benefitColor: '#16a34a'
}];
const tableRows = [{
  metric: 'Визитов детей в год',
  without: '2',
  with: '4-6'
}, {
  metric: 'No-show',
  without: '15-18%',
  with: '5-7%'
}, {
  metric: 'Взрослых с детской семьи',
  without: '0.8',
  with: '2.4'
}, {
  metric: 'Удержание семьи год 1',
  without: '60%',
  with: '88%'
}, {
  metric: 'LTV семьи за 5 лет',
  without: '350 000 ₸',
  with: '980 000 ₸'
}];
const timelineSteps = [{
  days: '1-2',
  icon: '📋',
  title: 'Брифинг',
  text: 'Изучаем клинику, настраиваем приложение под ваш бренд — цвета, логотип, название.'
}, {
  days: '3-5',
  icon: '⚙️',
  title: 'Настройка',
  text: 'Интеграция с системой записи. Настройка геймификации. Обучение персонала — 2 часа.'
}, {
  days: '6-8',
  icon: '🧪',
  title: 'Тест',
  text: 'Пилотный запуск с 10-20 первыми семьями. Проверяем все сценарии. Вносим правки.'
}, {
  days: '9-10',
  icon: '🚀',
  title: 'Запуск',
  text: 'Полноценный старт. Инструкция для персонала. Первые семьи скачивают и используют.'
}];
const faqs = [{
  q: 'Наши пациенты не будут скачивать приложение.',
  a: 'Это самое частое возражение. И самое неверное. Родители детей 3-12 лет — это миллениалы. Они живут в телефоне. Средняя конверсия в скачивание при правильном предложении на приёме составляет 60-70%. Мы обучаем персонал как это делать.'
}, {
  q: 'Сложно ли это внедрить?',
  a: '10 дней. Обучение персонала — 2 часа. Интеграция с большинством популярных систем записи — автоматическая. Вы не меняете процессы. Вы добавляете один инструмент.'
}, {
  q: 'Это дорого?',
  a: 'Одна удержанная семья окупает стоимость годовой подписки. Мы не продаём расходы. Мы продаём инвестицию с измеримым возвратом. На демо мы покажем расчёт конкретно для вашей клиники.'
}, {
  q: 'У нас маленькая клиника — нам это нужно?',
  a: 'Dental Kids особенно эффективен для небольших клиник. Крупные сети уже начинают внедрять технологии лояльности. У вас есть окно 12-18 месяцев чтобы занять позицию в своём районе до того как это сделает кто-то другой.'
}];
function formatNum(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace('.', ',') + ' млн ₸';
  if (n >= 1000) return Math.round(n / 1000) + ' 000 ₸';
  return n.toFixed(0) + ' ₸';
}
export function DentalKidsPage({
  onNavigate
}: DentalKidsPageProps) {
  const [patients, setPatients] = useState(50);
  const [check, setCheck] = useState(25000);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const lostFamilies = Math.round(patients * 0.37);
  const lostAdults = Math.round(lostFamilies * 2.4);
  const lostRevenue = lostFamilies * check * 2;
  const lostLTV = lostFamilies * check * 20;
  return <div className="min-h-screen bg-white" style={{
    fontFamily: "'Montserrat', sans-serif"
  }} data-id="element-71">
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" data-id="element-72" />

      <DentosHeader currentPage="dental-kids" onNavigate={onNavigate} data-id="element-73" />

      {/* ═══ SECTION 1 — HERO ═══ */}
      <section className="relative bg-[#0F1E3C] overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28" data-id="element-74">
        {/* Radial glow top right */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none" style={{
        background: 'radial-gradient(circle at 80% 20%, rgba(0,194,212,0.18) 0%, transparent 65%)'
      }} data-id="element-75" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] pointer-events-none" style={{
        background: 'radial-gradient(circle at 20% 80%, rgba(43,127,224,0.1) 0%, transparent 65%)'
      }} data-id="element-76" />

        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center" data-id="element-77">
          <span className="text-[#00C2D4] uppercase text-xs sm:text-sm tracking-[3px] mb-4 block" style={{
          fontWeight: 600
        }} data-id="element-78">
            DENTAL KIDS
          </span>
          <h1 className="text-white mb-6 leading-tight max-w-4xl mx-auto" style={{
          fontWeight: 900,
          fontSize: 'clamp(28px, 5vw, 58px)'
        }} data-id="element-79">
            Первая клиника в районе которая внедрит это — заберёт весь детский
            рынок
          </h1>
          <p className="text-white/65 max-w-[620px] mx-auto mb-10 leading-relaxed text-sm sm:text-base md:text-lg" style={{
          fontWeight: 400
        }} data-id="element-80">
            Пока ваши конкуренты борются за взрослых пациентов через скидки и
            рекламу — вы можете занять сегмент который кормит клинику следующие
            20 лет. Тихо. Системно. Навсегда.
          </p>
          <button onClick={() => window.open('https://wa.me/77055405178?text=Здравствуйте!%20Хочу%20получить%20демо%20Dental%20Kids', '_blank')} className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-xl text-sm sm:text-base transition-all hover:scale-[1.02] hover:brightness-110 mb-14" style={{
          background: '#00C2D4',
          fontWeight: 700
        }} data-id="element-81">
            Получить демо за 20 минут <ArrowRight size={18} data-id="element-82" />
          </button>

          {/* Stat boxes */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-83">
            {[{
            value: '730 визитов / год',
            label: 'с одной семьи за 20 лет'
          }, {
            value: '2.4 взрослых',
            label: 'приводит каждая детская семья'
          }, {
            value: '10 дней',
            label: 'от договора до первого пациента'
          }].map((s, i) => <div key={i} className="bg-white rounded-2xl px-5 py-4 shadow-lg text-left" data-id="element-84">
                <div className="text-[#0F1E3C] text-lg sm:text-xl" style={{
              fontWeight: 800
            }} data-id="element-85">
                  {s.value}
                </div>
                <div className="text-gray-500 text-xs sm:text-sm mt-1" style={{
              fontWeight: 400
            }} data-id="element-86">
                  {s.label}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 — СИТУАЦИЯ ═══ */}
      <section className="py-16 md:py-20 bg-[#F8FBFF]" data-id="element-87">
        <div className="container mx-auto px-4 md:px-6" data-id="element-88">
          <div className="text-center mb-12" data-id="element-89">
            <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-90">
              РЕАЛЬНОСТЬ РЫНКА
            </span>
            <h2 className="text-[#1A1A2E] mb-5 leading-tight max-w-2xl mx-auto" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-91">
              Детская стоматология — самый недооценённый актив в вашей клинике
            </h2>
            <p className="text-[#6B7280] max-w-[700px] mx-auto leading-[1.8] text-sm sm:text-base" style={{
            fontWeight: 400
          }} data-id="element-92">
              Казахстанский рынок детской стоматологии растёт на 15% в год.
              Специализированных детских клиник катастрофически мало — дефицит
              специалистов составляет 40% от потребности. 72% детей в возрасте
              6-7 лет имеют кариес и нуждаются в лечении.
              <br data-id="element-93" />
              <br data-id="element-94" />
              Это не проблема. Это рынок который почти никто не занял. Владельцы
              которые поймут это сейчас — через три года будут смотреть на
              конкурентов сверху вниз.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-95">
            {[{
            icon: '📊',
            iconBg: '#2B7FE0',
            title: '6 800+ стоматологий в Казахстане',
            text: 'Специализированных детских — единицы'
          }, {
            icon: '👶',
            iconBg: '#F97316',
            title: '72% детей 6-7 лет с кариесом',
            text: 'Огромный нереализованный спрос'
          }, {
            icon: '📈',
            iconBg: '#00C2D4',
            title: '+15% рост рынка ежегодно',
            text: 'Самый быстрорастущий сегмент'
          }].map((card, i) => <div key={i} className="bg-white rounded-2xl p-6 shadow-md" style={{
            boxShadow: '0 4px 24px rgba(43,127,224,0.08)'
          }} data-id="element-96">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl mb-4" style={{
              background: card.iconBg + '20'
            }} data-id="element-97">
                  {card.icon}
                </div>
                <h3 className="text-[#1A1A2E] text-base mb-2" style={{
              fontWeight: 700
            }} data-id="element-98">
                  {card.title}
                </h3>
                <p className="text-[#6B7280] text-sm" style={{
              fontWeight: 400
            }} data-id="element-99">
                  {card.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3 — ПРОБЛЕМА ═══ */}
      <section className="py-16 md:py-20 bg-white" data-id="element-100">
        <div className="container mx-auto px-4 md:px-6" data-id="element-101">
          <div className="text-center mb-10" data-id="element-102">
            <span className="text-[#F97316] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-103">
              ПРОБЛЕМА
            </span>
            <h2 className="text-[#1A1A2E] mb-5 leading-tight" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-104">
              Вы лечите детей.
              <br data-id="element-105" />
              Но семьи всё равно уходят.
            </h2>
            <p className="text-[#6B7280] max-w-[680px] mx-auto leading-[1.8] text-sm sm:text-base" style={{
            fontWeight: 400
          }} data-id="element-106">
              Вы хороший врач. Персонал вежливый. Клиника чистая. Но через
              полгода родители звонят в другое место. Почему?
              <br data-id="element-107" />
              <br data-id="element-108" />
              Потому что между визитами — тишина. Ребёнок забыл про вас.
              Родитель не думает о зубах пока не заболит. А когда заболит —
              гуглит заново и попадает к конкуренту.
              <br data-id="element-109" />
              <br data-id="element-110" />
              Вы не проигрываете в качестве лечения.
              <br data-id="element-111" />
              <strong className="text-[#1A1A2E]" data-id="element-112">
                Вы проигрываете в присутствии.
              </strong>
            </p>
          </div>

          <div className="w-16 h-px bg-gray-200 mx-auto mb-10" data-id="element-113" />

          {/* Big insight block */}
          <div className="bg-[#0F1E3C] rounded-3xl p-8 md:p-12 text-center max-w-3xl mx-auto" data-id="element-114">
            <p className="text-white/80 leading-[1.8] text-sm sm:text-base md:text-lg mb-8" style={{
            fontWeight: 400
          }} data-id="element-115">
              У Каспи есть приложение которое человек открывает 12 раз в день. У
              Freedom — экосистема в которой живут деньги семьи. У Nike — трекер
              который напоминает о себе каждое утро.
              <br data-id="element-116" />
              <br data-id="element-117" />
              Все они поняли одно и то же:
            </p>
            <blockquote className="text-white mb-8 leading-tight" style={{
            fontWeight: 800,
            fontSize: 'clamp(20px, 3vw, 34px)'
          }} data-id="element-118">
              "Кто владеет привычкой —<br data-id="element-119" />
              владеет клиентом."
            </blockquote>
            <p className="text-white/50 text-sm" style={{
            fontWeight: 400
          }} data-id="element-120">
              У вашей клиники пока нет этого инструмента.
              <br data-id="element-121" />У конкурента — тоже нет. Пока.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4 — КАЛЬКУЛЯТОР ═══ */}
      <section className="py-16 md:py-20 bg-[#F0F6FF]" data-id="element-122">
        <div className="container mx-auto px-4 md:px-6" data-id="element-123">
          <div className="text-center mb-10" data-id="element-124">
            <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-125">
              СЧИТАЙТЕ САМИ
            </span>
            <h2 className="text-[#1A1A2E] leading-tight max-w-xl mx-auto" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-126">
              Сколько вы теряете каждый месяц пока семья просто забыла про вас
            </h2>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 md:p-10 max-w-2xl mx-auto" style={{
          boxShadow: '0 8px 48px rgba(43,127,224,0.12)'
        }} data-id="element-127">
            {/* Slider 1 */}
            <div className="mb-8" data-id="element-128">
              <div className="flex items-center justify-between mb-3" data-id="element-129">
                <label className="text-[#1A1A2E] text-sm" style={{
                fontWeight: 600
              }} data-id="element-130">
                  Детских пациентов в месяц
                </label>
                <span className="text-[#00C2D4] text-3xl" style={{
                fontWeight: 800
              }} data-id="element-131">
                  {patients}
                </span>
              </div>
              <input type="range" min={10} max={200} value={patients} onChange={e => setPatients(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
              accentColor: '#00C2D4'
            }} data-id="element-132" />
              <div className="flex justify-between text-xs text-gray-400 mt-1" data-id="element-133">
                <span data-id="element-134">10</span>
                <span data-id="element-135">200</span>
              </div>
            </div>

            {/* Slider 2 */}
            <div className="mb-8" data-id="element-136">
              <div className="flex items-center justify-between mb-3" data-id="element-137">
                <label className="text-[#1A1A2E] text-sm" style={{
                fontWeight: 600
              }} data-id="element-138">
                  Средний чек детского приёма (₸)
                </label>
                <span className="text-[#00C2D4] text-3xl" style={{
                fontWeight: 800
              }} data-id="element-139">
                  {check.toLocaleString('ru')}
                </span>
              </div>
              <input type="range" min={15000} max={60000} step={1000} value={check} onChange={e => setCheck(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
              accentColor: '#00C2D4'
            }} data-id="element-140" />
              <div className="flex justify-between text-xs text-gray-400 mt-1" data-id="element-141">
                <span data-id="element-142">15 000 ₸</span>
                <span data-id="element-143">60 000 ₸</span>
              </div>
            </div>

            {/* Results */}
            <div className="grid grid-cols-2 gap-3" data-id="element-144">
              <div className="rounded-xl p-4" style={{
              background: '#EFF6FF'
            }} data-id="element-145">
                <div className="text-xs text-[#6B7280] mb-1" style={{
                fontWeight: 500
              }} data-id="element-146">
                  Семей потеряно за год
                </div>
                <div className="text-[#2B7FE0] text-2xl" style={{
                fontWeight: 800
              }} data-id="element-147">
                  {lostFamilies}
                </div>
              </div>
              <div className="rounded-xl p-4" style={{
              background: '#FFF7ED'
            }} data-id="element-148">
                <div className="text-xs text-[#6B7280] mb-1" style={{
                fontWeight: 500
              }} data-id="element-149">
                  Взрослых пациентов не получено
                </div>
                <div className="text-[#F97316] text-2xl" style={{
                fontWeight: 800
              }} data-id="element-150">
                  {lostAdults}
                </div>
              </div>
              <div className="rounded-xl p-4" style={{
              background: '#0F1E3C'
            }} data-id="element-151">
                <div className="text-xs text-white/60 mb-1" style={{
                fontWeight: 500
              }} data-id="element-152">
                  Потерянная выручка за год
                </div>
                <div className="text-white text-xl" style={{
                fontWeight: 800
              }} data-id="element-153">
                  {formatNum(lostRevenue)}
                </div>
              </div>
              <div className="rounded-xl p-4" style={{
              background: '#00C2D4'
            }} data-id="element-154">
                <div className="text-xs text-white/80 mb-1" style={{
                fontWeight: 500
              }} data-id="element-155">
                  Потерянный LTV за 20 лет
                </div>
                <div className="text-white text-xl" style={{
                fontWeight: 800
              }} data-id="element-156">
                  {formatNum(lostLTV)}
                </div>
              </div>
            </div>

            <p className="text-[#6B7280] text-xs italic mt-5 leading-relaxed" style={{
            fontWeight: 400
          }} data-id="element-157">
              По данным DENTOS Research клиники без системы удержания теряют
              35-40% детских семей в первый год. Каждая потерянная семья — это
              не один визит. Это 20 лет детских приёмов, родители, братья,
              сёстры и их рекомендации.
            </p>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5 — РЕШЕНИЕ ═══ */}
      <section className="py-16 md:py-20 bg-white" data-id="element-158">
        <div className="container mx-auto px-4 md:px-6" data-id="element-159">
          <div className="text-center mb-12" data-id="element-160">
            <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-161">
              РЕШЕНИЕ
            </span>
            <h2 className="text-[#1A1A2E] mb-4 leading-tight max-w-2xl mx-auto" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-162">
              Dental Kids — приложение которое держит вашу клинику в телефоне
              каждой семьи
            </h2>
            <p className="text-[#6B7280] max-w-xl mx-auto text-sm sm:text-base" style={{
            fontWeight: 400
          }} data-id="element-163">
              Не маркетинговый инструмент. Не CRM.
              <br data-id="element-164" />
              Экосистема лояльности которая работает 24/7 пока вы принимаете
              других пациентов.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto" data-id="element-165">
            {features.map((f, i) => <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group" style={{
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)'
          }} data-id="element-166">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform" style={{
              background: f.iconBg + '20'
            }} data-id="element-167">
                  {f.icon}
                </div>
                <h3 className="text-[#1A1A2E] text-base mb-2" style={{
              fontWeight: 700
            }} data-id="element-168">
                  {f.title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-4" style={{
              fontWeight: 400
            }} data-id="element-169">
                  {f.description}
                </p>
                <span className="inline-block text-xs px-3 py-1 rounded-full" style={{
              background: '#dcfce7',
              color: '#16a34a',
              fontWeight: 600
            }} data-id="element-170">
                  {f.benefit}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6 — ЧАСТОТА / ГИГАНТЫ ═══ */}
      <section className="py-20 md:py-28 bg-[#0F1E3C] text-center" data-id="element-171">
        <div className="container mx-auto px-4 md:px-6" data-id="element-172">
          <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-4 block" style={{
          fontWeight: 600
        }} data-id="element-173">
            ГЛАВНЫЙ СЕКРЕТ
          </span>
          <h2 className="text-white mb-8 leading-tight max-w-3xl mx-auto" style={{
          fontWeight: 800,
          fontSize: 'clamp(22px, 4vw, 46px)'
        }} data-id="element-174">
            Каспи открывают 12 раз в день.
            <br data-id="element-175" />
            Nike — каждое утро. Freedom — при каждой покупке.
            <br data-id="element-176" />
            Вы думаете это случайность?
          </h2>
          <p className="text-white/65 max-w-[720px] mx-auto leading-[1.9] text-sm sm:text-base md:text-lg mb-10" style={{
          fontWeight: 400
        }} data-id="element-177">
            Самые дорогие компании мира построены не на продукте. Они построены
            на привычке.
            <br data-id="element-178" />
            <br data-id="element-179" />
            Каспи стал банком номер один в Казахстане не потому что лучшие
            проценты. А потому что их приложение стало частью ежедневной жизни
            каждой семьи. Уйти невозможно. Не потому что нельзя. А потому что
            незачем.
            <br data-id="element-180" />
            <br data-id="element-181" />
            Nike не продаёт кроссовки. Nike продаёт трекер активности который
            напоминает вам каждое утро что вы спортсмен.
            <br data-id="element-182" />
            <br data-id="element-183" />
            Freedom построил экосистему в которой живут деньги, инвестиции и
            страховка казахстанских семей. Переключиться технически можно. Но
            зачем если всё уже здесь?
          </p>

          {/* Logo boxes */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12" data-id="element-184">
            {[{
            name: 'Каспи',
            sub: '12 открытий/день'
          }, {
            name: 'Nike',
            sub: 'каждое утро'
          }, {
            name: 'Freedom',
            sub: 'экосистема семьи'
          }].map((b, i) => <div key={i} className="px-6 py-4 rounded-2xl text-center" style={{
            background: 'rgba(255,255,255,0.08)'
          }} data-id="element-185">
                <div className="text-white text-base" style={{
              fontWeight: 700
            }} data-id="element-186">
                  {b.name}
                </div>
                <div className="text-white/50 text-xs mt-1" style={{
              fontWeight: 400
            }} data-id="element-187">
                  {b.sub}
                </div>
              </div>)}
          </div>

          <div className="text-[#00C2D4] mb-10 max-w-2xl mx-auto leading-tight" style={{
          fontWeight: 800,
          fontSize: 'clamp(18px, 2.5vw, 30px)'
        }} data-id="element-188">
            "Не тот кто лучше лечит — лидирует на рынке.
            <br data-id="element-189" />
            Тот кто чаще присутствует в жизни клиента."
          </div>

          {/* Two stat boxes */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10" data-id="element-190">
            <div className="px-6 py-4 rounded-2xl border border-[#00C2D4]/40 text-center" style={{
            background: 'rgba(255,255,255,0.06)'
          }} data-id="element-191">
              <div className="text-[#00C2D4] text-xl" style={{
              fontWeight: 800
            }} data-id="element-192">
                8-12 раз в месяц
              </div>
              <div className="text-white/60 text-xs mt-1" style={{
              fontWeight: 400
            }} data-id="element-193">
                открывают Dental Kids
              </div>
            </div>
            <div className="px-6 py-4 rounded-2xl border border-gray-600 text-center" style={{
            background: 'rgba(255,255,255,0.06)'
          }} data-id="element-194">
              <div className="text-white text-xl" style={{
              fontWeight: 800
            }} data-id="element-195">
                2 раза в год
              </div>
              <div className="text-white/60 text-xs mt-1" style={{
              fontWeight: 400
            }} data-id="element-196">
                думают о стоматологии без приложения
              </div>
            </div>
          </div>

          <p className="text-white/40 text-sm italic max-w-xl mx-auto" style={{
          fontWeight: 400
        }} data-id="element-197">
            Разница между этими двумя цифрами — это разница между клиникой
            которую помнят и клиникой которую забыли.
          </p>
        </div>
      </section>

      {/* ═══ SECTION 7 — ЭКОНОМИКА ═══ */}
      <section className="py-16 md:py-20 bg-[#F8FBFF]" data-id="element-198">
        <div className="container mx-auto px-4 md:px-6" data-id="element-199">
          <div className="text-center mb-10" data-id="element-200">
            <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-201">
              ЭКОНОМИКА
            </span>
            <h2 className="text-[#1A1A2E] leading-tight" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-202">
              Что Dental Kids даёт вашей клинике в цифрах
            </h2>
          </div>

          {/* Comparison table */}
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden mb-8" style={{
          boxShadow: '0 4px 32px rgba(0,0,0,0.08)'
        }} data-id="element-203">
            <div className="overflow-x-auto" data-id="element-204">
              <table className="w-full min-w-[400px]" data-id="element-205">
                <thead data-id="element-206">
                  <tr style={{
                  background: '#0F1E3C'
                }} data-id="element-207">
                    <th className="px-4 py-4 text-left text-white text-sm" style={{
                    fontWeight: 600
                  }} data-id="element-208">
                      Показатель
                    </th>
                    <th className="px-4 py-4 text-center text-white text-sm" style={{
                    fontWeight: 600
                  }} data-id="element-209">
                      Без Dental Kids
                    </th>
                    <th className="px-4 py-4 text-center text-[#00C2D4] text-sm" style={{
                    fontWeight: 700
                  }} data-id="element-210">
                      С Dental Kids ✅
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-211">
                  {tableRows.map((row, i) => <tr key={i} style={{
                  background: i % 2 === 0 ? '#ffffff' : '#F8FBFF'
                }} data-id="element-212">
                      <td className="px-4 py-3.5 text-[#1A1A2E] text-sm" style={{
                    fontWeight: 500
                  }} data-id="element-213">
                        {row.metric}
                      </td>
                      <td className="px-4 py-3.5 text-center text-[#6B7280] text-sm" style={{
                    fontWeight: 400
                  }} data-id="element-214">
                        {row.without}
                      </td>
                      <td className="px-4 py-3.5 text-center text-[#00C2D4] text-sm" style={{
                    fontWeight: 700
                  }} data-id="element-215">
                        {row.with}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Insight box */}
          <div className="max-w-3xl mx-auto bg-[#0F1E3C] rounded-2xl p-6 sm:p-8 md:p-10" data-id="element-216">
            <p className="text-white leading-[1.8] text-sm sm:text-base mb-7" style={{
            fontWeight: 400
          }} data-id="element-217">
              Клиника с 50 детскими пациентами в месяц которая внедрила Dental
              Kids получает в течение первого года дополнительно:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-218">
              {[{
              value: '+480 визитов',
              label: 'от роста частоты'
            }, {
              value: '+120 взрослых',
              label: 'от семейного эффекта'
            }, {
              value: '+12 600 000 ₸',
              label: 'дополнительной выручки'
            }].map((s, i) => <div key={i} className="text-center" data-id="element-219">
                  <div className="text-[#00C2D4] text-xl sm:text-2xl" style={{
                fontWeight: 800
              }} data-id="element-220">
                    {s.value}
                  </div>
                  <div className="text-white/50 text-xs mt-1" style={{
                fontWeight: 400
              }} data-id="element-221">
                    {s.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 8 — ВНЕДРЕНИЕ ═══ */}
      <section className="py-16 md:py-20 bg-white" data-id="element-222">
        <div className="container mx-auto px-4 md:px-6" data-id="element-223">
          <div className="text-center mb-12" data-id="element-224">
            <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-225">
              КАК ЭТО РАБОТАЕТ
            </span>
            <h2 className="text-[#1A1A2E] leading-tight" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-226">
              10 дней от договора до первого
              <br data-id="element-227" />
              пациента через приложение
            </h2>
          </div>

          {/* Timeline */}
          <div className="max-w-4xl mx-auto" data-id="element-228">
            {/* Desktop: horizontal */}
            <div className="hidden md:flex items-start gap-0 mb-10" data-id="element-229">
              {timelineSteps.map((step, i) => <div key={i} className="flex-1 relative" data-id="element-230">
                  {/* Connector line */}
                  {i < timelineSteps.length - 1 && <div className="absolute top-7 left-1/2 w-full h-0.5 bg-gradient-to-r from-[#00C2D4] to-[#2B7FE0]" style={{
                zIndex: 0
              }} data-id="element-231" />}
                  <div className="flex flex-col items-center text-center px-4 relative z-10" data-id="element-232">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-sm mb-4 shadow-lg" style={{
                  background: 'linear-gradient(135deg, #00C2D4, #2B7FE0)',
                  fontWeight: 800
                }} data-id="element-233">
                      {step.days}
                    </div>
                    <div className="text-2xl mb-2" data-id="element-234">{step.icon}</div>
                    <h3 className="text-[#1A1A2E] text-base mb-2" style={{
                  fontWeight: 700
                }} data-id="element-235">
                      {step.title}
                    </h3>
                    <p className="text-[#6B7280] text-xs leading-relaxed" style={{
                  fontWeight: 400
                }} data-id="element-236">
                      {step.text}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Mobile: vertical */}
            <div className="md:hidden space-y-6 mb-10" data-id="element-237">
              {timelineSteps.map((step, i) => <div key={i} className="flex gap-4" data-id="element-238">
                  <div className="flex flex-col items-center" data-id="element-239">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0 shadow-md" style={{
                  background: 'linear-gradient(135deg, #00C2D4, #2B7FE0)',
                  fontWeight: 800
                }} data-id="element-240">
                      {step.days}
                    </div>
                    {i < timelineSteps.length - 1 && <div className="w-0.5 flex-1 bg-gradient-to-b from-[#00C2D4] to-[#2B7FE0] mt-2 min-h-[32px]" data-id="element-241" />}
                  </div>
                  <div className="pb-4" data-id="element-242">
                    <div className="text-xl mb-1" data-id="element-243">{step.icon}</div>
                    <h3 className="text-[#1A1A2E] text-base mb-1" style={{
                  fontWeight: 700
                }} data-id="element-244">
                      {step.title}
                    </h3>
                    <p className="text-[#6B7280] text-sm leading-relaxed" style={{
                  fontWeight: 400
                }} data-id="element-245">
                      {step.text}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Result box */}
            <div className="rounded-2xl p-6 sm:p-8 text-center text-white" style={{
            background: '#00C2D4'
          }} data-id="element-246">
              <p className="text-base sm:text-lg leading-relaxed" style={{
              fontWeight: 600
            }} data-id="element-247">
                День 30 — первые результаты:
              </p>
              <p className="text-white/80 text-sm sm:text-base mt-2 leading-relaxed" style={{
              fontWeight: 400
            }} data-id="element-248">
                количество семей в приложении, частота открытий,
                <br data-id="element-249" />
                повторные записи через приложение, снижение no-show.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 9 — FAQ ═══ */}
      <section className="py-16 md:py-20 bg-[#F0F6FF]" data-id="element-250">
        <div className="container mx-auto px-4 md:px-6" data-id="element-251">
          <div className="text-center mb-10" data-id="element-252">
            <span className="text-[#00C2D4] uppercase text-xs tracking-[3px] mb-3 block" style={{
            fontWeight: 600
          }} data-id="element-253">
              ЧАСТЫЕ ВОПРОСЫ
            </span>
            <h2 className="text-[#1A1A2E] leading-tight" style={{
            fontWeight: 800,
            fontSize: 'clamp(22px, 3.5vw, 40px)'
          }} data-id="element-254">
              Ответы на главные возражения
            </h2>
          </div>

          <div className="max-w-2xl mx-auto space-y-3" data-id="element-255">
            {faqs.map((faq, i) => <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
          }} data-id="element-256">
                <button className="w-full flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-257">
                  <span className="text-[#1A1A2E] text-sm sm:text-base pr-4" style={{
                fontWeight: 600
              }} data-id="element-258">
                    {faq.q}
                  </span>
                  <span className="flex-shrink-0 text-[#00C2D4]" data-id="element-259">
                    {openFaq === i ? <ChevronUp size={20} data-id="element-260" /> : <ChevronDown size={20} data-id="element-261" />}
                  </span>
                </button>
                <div className="overflow-hidden transition-all duration-300" style={{
              maxHeight: openFaq === i ? '300px' : '0px'
            }} data-id="element-262">
                  <p className="px-5 sm:px-6 pb-5 text-[#6B7280] text-sm leading-relaxed" style={{
                fontWeight: 400
              }} data-id="element-263">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 10 — ФИНАЛЬНЫЙ CTA ═══ */}
      <section className="py-20 md:py-28 text-center relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0F1E3C 0%, #1a3a6b 100%)'
    }} data-id="element-264">
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,194,212,0.1) 0%, transparent 70%)'
      }} data-id="element-265" />
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-266">
          <h2 className="text-white mb-6 leading-tight max-w-3xl mx-auto" style={{
          fontWeight: 900,
          fontSize: 'clamp(24px, 4vw, 50px)'
        }} data-id="element-267">
            Первая клиника в вашем районе которая
            <br data-id="element-268" />
            внедрит Dental Kids — заберёт рынок
          </h2>
          <p className="text-white/65 max-w-[600px] mx-auto mb-10 leading-[1.8] text-sm sm:text-base" style={{
          fontWeight: 400
        }} data-id="element-269">
            Не потому что будет лучше лечить. А потому что будет присутствовать
            в жизни каждой семьи каждый день. Пока конкуренты тратят бюджеты на
            рекламу — вы будете расти за счёт удержания и рекомендаций.
            <br data-id="element-270" />
            <br data-id="element-271" />
            Демо занимает 20 минут. Вы увидите продукт, получите расчёт для
            вашей клиники и поймёте подходит ли это вам. Никаких обязательств.
          </p>
          <button onClick={() => window.open('https://wa.me/77055405178?text=Здравствуйте!%20Хочу%20получить%20демо%20Dental%20Kids', '_blank')} className="inline-flex items-center gap-2 text-white px-10 py-5 rounded-2xl text-base sm:text-lg transition-all hover:scale-[1.02] hover:brightness-110 mb-5" style={{
          background: '#00C2D4',
          fontWeight: 700
        }} data-id="element-272">
            Получить демо за 20 минут <ArrowRight size={20} data-id="element-273" />
          </button>
          <p className="text-white/40 text-xs" style={{
          fontWeight: 400
        }} data-id="element-274">
            Отвечаем в течение 2 часов в рабочее время.
            <br data-id="element-275" />
            Демо проводим онлайн или в вашей клинике.
          </p>
        </div>
      </section>

      <DentosFooter onNavigate={onNavigate} data-id="element-276" />

      {/* Scroll to top */}
      {showScrollTop && <button onClick={() => window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })} className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110" style={{
      background: '#00C2D4'
    }} aria-label="Наверх" data-id="element-277">
          <ArrowUp size={20} color="#fff" data-id="element-278" />
        </button>}
    </div>;
}